import { gql } from "@apollo/client";

// ============================================================================
// ARTICLE QUERIES
// ============================================================================

import { GENERAL_FIELDS, SITE_FIELDS, PAGE_SETTINGS, ARTICLE_FIELDS } from "./shared";
import { SECTIONS_WRAPPER_QUERY } from "./fragments/sections";

// ============================================================================
// ARTICLE FRAGMENTS
// ============================================================================

export const ARTICLE_TEASER_IMAGE = gql`
  fragment ArticleTeaserImage on ArticleRecord {
    introImage {
      id
      responsiveImage(
        imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
      ) {
        alt
        height
        src
        title
        width
      }
    }
  }
`;

export const ARTICLE_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${ARTICLE_FIELDS}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  query GetArticle($slug: String!, $locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: article(filter: { slug: { eq: $slug } }, locale: $locale) {
      # Article Meta (SEO)
      slugs: _allSlugLocales(locale: $locale) {
        locale
        value
      }
      titles: _allTitleLocales(locale: $locale) {
        locale
        value
      }
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Article Fields (Content)
      ...ArticleFields
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "800", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }

      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
    }
  }
`;

export const ARTICLE_RELATED_QUERY = gql`
  ${ARTICLE_FIELDS}
  ${ARTICLE_TEASER_IMAGE}
  query GetRelatedArticles($locale: SiteLocale!, $tagIds: [ItemId!], $exclude: ItemId) {
    relatedArticles: allArticles(
      filter: {
        _locales: { allIn: [$locale] }
        OR: [{ tag: { in: $tagIds } }, { secondaryTags: { anyIn: $tagIds } }]
        id: { neq: $exclude }
      }
      locale: $locale
      first: 3
      orderBy: _createdAt_DESC
    ) {
      ...ArticleFields
      ...ArticleTeaserImage
    }
  }
`;

export const LATEST_ARTICLES_QUERY = gql`
  ${ARTICLE_FIELDS}
  ${ARTICLE_TEASER_IMAGE}
  query GetLatestArticles($locale: SiteLocale!) {
    latestArticles: allArticles(
      filter: { _locales: { allIn: [$locale] } }
      locale: $locale
      first: 3
      orderBy: _createdAt_DESC
    ) {
      ...ArticleFields
      ...ArticleTeaserImage
    }
  }
`;

export const ARTICLES_OVERVIEW_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${ARTICLE_FIELDS}
  ${ARTICLE_TEASER_IMAGE}
  query GetArticles($locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: articlesOverview(locale: $locale) {
      # Articles Overview Meta (SEO)
      titles: _allTitleLocales(locale: $locale) {
        locale
        value
      }
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Articles Overview Fields (Content)
      title
      shortTitle

      # Articles Overview Components
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
    }
    articles: allArticles(
      locale: $locale
      first: 100
      orderBy: _createdAt_DESC
      filter: { _locales: { allIn: [$locale] } }
    ) {
      ...ArticleFields
      ...ArticleTeaserImage
    }
    articlesMeta: _allArticlesMeta(filter: { _locales: { allIn: [$locale] } }) {
      count
    }
  }
`;

export const ARTICLES_FOR_TAG_FILTER_QUERY = gql`
  query ArticlesForTagFilter($locale: SiteLocale!, $first: IntType!, $skip: IntType!) {
    articles: allArticles(
      locale: $locale
      first: $first
      skip: $skip
      orderBy: _createdAt_DESC
      filter: { _locales: { allIn: [$locale] } }
    ) {
      tag {
        id
        title
      }
      secondaryTags {
        id
        title
      }
    }
  }
`;
